/**
 * trønso: Autoplay-Videos erst laden, wenn sie ins Bild kommen.
 *
 * Gegenstück zu `snippets/video.liquid`: Dort stehen Autoplay-Videos mit
 * `preload="none"` und ohne `autoplay` im Markup, markiert mit
 * `data-tronso-autoplay`. Hier wird abgespielt, sobald ein Video den Viewport
 * erreicht, und angehalten, sobald es ihn verlässt.
 *
 * - Kopien, die nie sichtbar werden (Zoom-Dialog, Raster auf dem Handy, Karussell
 *   auf dem Desktop), laden dadurch gar nicht.
 * - Hält jemand ein Video selbst an, startet es beim Zurückscrollen nicht von
 *   allein wieder.
 * - Pausen von außen laufen wieder an (geändert am 2026-10-28). Atelier hält beim
 *   Schließen des Zoom-Dialogs jedes Galerie-Video an (`assets/media.js`), der
 *   Browser beim Wechsel in einen anderen Tab oder eine andere App. Vorher galt
 *   jede fremde Pause als Pause des Besuchers, und das Video blieb für den Rest
 *   des Besuchs stehen. Als Pause des Besuchers zählt jetzt nur noch eine, der
 *   kurz vorher ein Tippen, Klicken oder Tastendruck auf das Video vorausging.
 * - Verweigert der Browser das Abspielen (iOS im Stromsparmodus, Datensparmodus),
 *   startet das Video mit der nächsten Berührung der Seite.
 * - Neu gerenderte Galerien (Variantenwechsel, Section Rendering) werden über
 *   einen MutationObserver mit erfasst.
 * - Läuft als Modul: Das Snippet bindet die Datei pro Video ein, ausgeführt wird
 *   sie trotzdem nur einmal.
 */

const SELECTOR = 'video[data-tronso-autoplay]';

/** So lange nach einer Eingabe auf dem Video gilt eine Pause als gewollt (ms). */
const USER_PAUSE_WINDOW = 1000;

/** Videos, die gerade vom Skript angehalten werden, nicht vom Besucher. */
const pausingBySelf = new WeakSet();

/** Videos, die der Besucher selbst angehalten hat. */
const pausedByUser = new WeakSet();

/** Letzte Eingabe des Besuchers auf dem Video oder seiner Hülle. */
const lastInput = new WeakMap();

/** Videos, die gerade im Viewport stehen. */
const visible = new Set();

/** Videos, deren Start der Browser ohne Eingabe verweigert hat. */
const blocked = new Set();

/** @param {HTMLVideoElement} video */
function start(video) {
  if (!video.isConnected) {
    visible.delete(video);
    return;
  }
  if (!visible.has(video) || pausedByUser.has(video) || document.hidden) return;
  if (!video.paused) return;

  // Ohne `muted` als Eigenschaft verweigern Browser den Autostart. Das Attribut
  // allein reicht nicht bei Knoten, die per Skript geklont oder ersetzt wurden.
  video.muted = true;
  if (video.preload === 'none') video.preload = 'auto';

  video.play().then(
    () => blocked.delete(video),
    (error) => {
      if (error?.name === 'NotAllowedError') {
        blocked.add(video);
        waitForGesture();
      }
    }
  );
}

let waitingForGesture = false;

function waitForGesture() {
  if (waitingForGesture) return;
  waitingForGesture = true;

  const events = ['touchend', 'click', 'keydown'];
  const retry = () => {
    for (const type of events) document.removeEventListener(type, retry, true);
    waitingForGesture = false;
    for (const video of [...blocked]) {
      blocked.delete(video);
      start(video);
    }
  };
  for (const type of events) document.addEventListener(type, retry, { capture: true, passive: true });
}

const visibility = new IntersectionObserver(
  (entries) => {
    for (const { target, isIntersecting } of entries) {
      if (!(target instanceof HTMLVideoElement)) continue;

      if (isIntersecting) {
        visible.add(target);
        start(target);
      } else {
        visible.delete(target);
        blocked.delete(target);
        if (!target.paused) {
          pausingBySelf.add(target);
          target.pause();
        }
      }
    }
  },
  { threshold: 0.25 }
);

/** @param {HTMLVideoElement} video */
function watch(video) {
  if (video.dataset.tronsoWatched) return;
  video.dataset.tronsoWatched = '';

  const markInput = () => lastInput.set(video, performance.now());
  const surface = video.closest('deferred-media') ?? video;
  surface.addEventListener('pointerdown', markInput, { passive: true });
  surface.addEventListener('keydown', markInput);

  video.addEventListener('pause', () => {
    if (pausingBySelf.delete(video) || video.ended) return;

    const input = lastInput.get(video);
    if (input !== undefined && performance.now() - input < USER_PAUSE_WINDOW) {
      pausedByUser.add(video);
      return;
    }

    // Pause von außen: im nächsten Frame wieder starten. In einem Tab im
    // Hintergrund läuft der Frame erst, wenn die Seite wieder sichtbar ist.
    requestAnimationFrame(() => start(video));
  });
  video.addEventListener('play', () => pausedByUser.delete(video));

  visibility.observe(video);
}

function scan() {
  for (const video of document.querySelectorAll(SELECTOR)) {
    if (video instanceof HTMLVideoElement) watch(video);
  }
  // Entfernte Videos nicht festhalten.
  for (const video of visible) {
    if (!video.isConnected) visible.delete(video);
  }
}

scan();

document.addEventListener('visibilitychange', () => {
  if (document.hidden) return;
  for (const video of visible) start(video);
});

let queued = false;
new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    scan();
  });
}).observe(document.documentElement, { childList: true, subtree: true });
